import { Option } from "@/components/primitives/select";
import { removeLeadingZeroDecimal } from "@/utils";
import { truncateName } from "@/utils/injuries";
import { Sport, TeamOverview, TopLeaders } from "common";

export interface TeamOverviewCardData {
  category: string;
  rank: number;
  value: string;
  unit: string;
}
export function getTeamOverviewCardData(
  data: TeamOverview
): TeamOverviewCardData[] {
  switch (data.sport) {
    case "nfl":
      return [
        {
          category: "Yards / Game",
          rank: data.yardsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.yardsPerGame.value as number)}`,
          unit: "YDS/G",
        },
        {
          category: "Passing Yards / Game",
          rank: data.passingYardsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.passingYardsPerGame.value as number)}`,
          unit: "PY/G",
        },
        {
          category: "Rushing Yards / Game",
          rank: data.rushingYardsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.rushingYardsPerGame.value as number)}`,
          unit: "RY/G",
        },
        {
          category: "Points / Game",
          rank: data.pointsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.pointsPerGame.value as number)}`,
          unit: "PTS/G",
        },
        {
          category: "Time of Possession / Game",
          rank: data.timeOfPossessionPerGame.rank,
          value: `${data.timeOfPossessionPerGame.value}`,
          unit: "TOP/G",
        },
      ];
    case "nba":
      return [
        {
          category: "Points / Game",
          rank: data.pointsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.pointsPerGame.value as number)}`,
          unit: "PTS/G",
        },
        {
          category: "Field Goal %",
          rank: data.fieldGoalPctPerGame?.rank,
          value: `${removeLeadingZeroDecimal(data.fieldGoalPctPerGame?.value as number)}`,
          unit: "FG%",
        },
        {
          category: "3 Point %",
          rank: data.threePointerPctPerGame?.rank,
          value: `${removeLeadingZeroDecimal(data.threePointerPctPerGame?.value as number)}`,
          unit: "3P%",
        },
        {
          category: "Free Throw %",
          rank: data.freeThrowPctPerGame?.rank,
          value: `${removeLeadingZeroDecimal(data.freeThrowPctPerGame?.value as number)}`,
          unit: "FT%",
        },
        {
          category: "Rebounds / Game",
          rank: data.reboundsPerGame?.rank,
          value: `${removeLeadingZeroDecimal(data.reboundsPerGame?.value as number)}`,
          unit: "REB/G",
        },
      ];
    case "mlb":
      return [
        {
          category: "Batting Average",
          rank: data.battingAverage?.rank,
          value: `${removeLeadingZeroDecimal(data.battingAverage?.value as number)}`,
          unit: "BA",
        },
        {
          category: "Home Runs",
          rank: data.homeRuns?.rank,
          value: `${removeLeadingZeroDecimal(data.homeRuns?.value as number)}`,
          unit: "HR",
        },
        {
          category: "Runs Batted In",
          rank: data.runsBattedIn?.rank,
          value: `${data.runsBattedIn?.value as number}`,
          unit: "RBI",
        },
        {
          category: "Earned Run Average",
          rank: data.earnedRunAverage?.rank,
          value: `${removeLeadingZeroDecimal(data.earnedRunAverage?.value as number)}`,
          unit: "ERA",
        },
        {
          category: "Pitching Strikeouts",
          rank: data.pitchingStrikeouts?.rank,
          value: `${data.pitchingStrikeouts?.value as number}`,
          unit: "PSO",
        },
      ];
    case "nhl":
      return [
        {
          category: "Points",
          rank: data.rankedPoints.rank || 0,
          value: `${data.rankedPoints.value || "N/A"}`,
          unit: "PTS",
        },
        {
          category: "Goals / Game",
          rank: data.goalsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.goalsPerGame.value as number)}`,
          unit: "G/G",
        },
        {
          category: "Power Play Goals",
          rank: data.powerPlayGoals?.rank,
          value: `${removeLeadingZeroDecimal(data.powerPlayGoals?.value as number)}`,
          unit: "PPG",
        },
        {
          category: "Goals Against / Game",
          rank: data.opponentGoalsPerGame.rank,
          value: `${removeLeadingZeroDecimal(data.opponentGoalsPerGame.value as number)}`,
          unit: "GA/G",
        },
        {
          category: "Save Percentage",
          rank: data.savePct.rank,
          value: `${removeLeadingZeroDecimal(data.savePct.value as number)}`,
          unit: "SV%",
        },
        
      ];
    default:
      return [];
  }
}
export function getTeamTopLeadersCategories(sport: Sport): {
  category1: string;
  category2: string;
} {
  switch (sport) {
    case "nfl":
    case "nba":
      return {
        category1: "Offense",
        category2: "Defense",
      };
    case "mlb":
      return {
        category1: "Batting",
        category2: "Pitching",
      };
    case "nhl":
      return {
        category1: "Skating",
        category2: "Goalie",
      };
    default:
      return { category1: "", category2: "" };
  }
}
export interface TeamTopLeaderData extends Option {
  name: string;
  position: string;
  abbreviation: string;
  stat1: Option;
  stat2?: Option;
  sport: Sport;
}
export function getTeamTopLeaderData(data: TopLeaders): TeamTopLeaderData[] {
  switch (data.sport) {
    case "nfl":
      return [
        {
          label: "Passing Yards",
          value: `${data.passingYards.value}`,
          name: `${truncateName(data.passingYards.firstName)} ${data.passingYards.lastName}`,
          position: `${data.passingYards.position} ${data.passingYards.number}`,
          abbreviation: "PY",
          stat1: {
            label: "TD",
            value: `${data.passingYards.touchdowns}`,
          },
          stat2: {
            label: "QBR",
            value: `${data.passingYards.qbRating}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Rushing Yards",
          value: `${data.rushingYards.value}`,
          name: `${truncateName(data.rushingYards.firstName)} ${data.rushingYards.lastName}`,
          position: `${data.rushingYards.position} ${data.rushingYards.number}`,
          abbreviation: "RY",
          stat1: {
            label: "TD",
            value: `${data.rushingYards.touchdowns}`,
          },
          stat2: {
            label: "CAR",
            value: `${data.rushingYards.carries}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Receiving Yards",
          value: `${data.receivingYards.value}`,
          name: `${truncateName(data.receivingYards.firstName)} ${data.receivingYards.lastName}`,
          position: `${data.receivingYards.position} ${data.receivingYards.number}`,
          abbreviation: "RECY",
          stat1: {
            label: "TD",
            value: `${data.receivingYards.touchdowns}`,
          },
          stat2: {
            label: "REC",
            value: `${data.receivingYards.receptions}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Tackles",
          value: `${data.tackles.value}`,
          name: `${truncateName(data.tackles.firstName)} ${data.tackles.lastName}`,
          position: `${data.tackles.position} ${data.tackles.number}`,
          abbreviation: "TKL",
          stat1: {
            label: "SCK",
            value: `${data.tackles.sacks}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Sacks",
          value: `${data.sacks.value}`,
          name: `${truncateName(data.sacks.firstName)} ${data.sacks.lastName}`,
          position: `${data.sacks.position} ${data.sacks.number}`,
          abbreviation: "SCK",
          stat1: {
            label: "TKL",
            value: `${data.sacks.tackles}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Interceptions",
          value: `${data.interceptions.value}`,
          name: `${truncateName(data.interceptions.firstName)} ${data.interceptions.lastName}`,
          position: `${data.interceptions.position} ${data.interceptions.number}`,
          abbreviation: "INT",
          stat1: {
            label: "TKL",
            value: `${data.interceptions.tackles}`,
          },
          sport: `${data.sport}`,
        },
      ];
    case "nba":
      return [
        {
          label: "Points / Game",
          value: `${data.pointsPerGame?.value}`,
          name: `${truncateName(data.pointsPerGame?.firstName)} ${data.pointsPerGame?.lastName}`,
          position: `${data.pointsPerGame?.position} ${data.pointsPerGame?.number}`,
          abbreviation: "PTS/G",
          stat1: {
            label: "AST/G",
            value: `${data.pointsPerGame?.assistsPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Assists / Game",
          value: `${data.assistsPerGame?.value}`,
          name: `${truncateName(data.assistsPerGame?.firstName)} ${data.assistsPerGame?.lastName}`,
          position: `${data.assistsPerGame?.position} ${data.assistsPerGame?.number}`,
          abbreviation: "AST/G",
          stat1: {
            label: "PTS/G",
            value: `${data.assistsPerGame?.pointsPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "3 Pointers / Game",
          value: `${data.threePointersPerGame?.value}`,
          name: `${truncateName(data.threePointersPerGame?.firstName)} ${data.threePointersPerGame?.lastName}`,
          position: `${data.threePointersPerGame?.position} ${data.threePointersPerGame?.number}`,
          abbreviation: "3P/G",
          stat1: {
            label: "PTS/G",
            value: `${data.threePointersPerGame?.pointsPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Defensive Rebounds / Game",
          value: `${data.defensiveReboundsPerGame?.value}`,
          name: `${truncateName(data.defensiveReboundsPerGame?.firstName)} ${data.defensiveReboundsPerGame?.lastName}`,
          position: `${data.defensiveReboundsPerGame?.position} ${data.defensiveReboundsPerGame?.number}`,
          abbreviation: "DR/G",
          stat1: {
            label: "BLK/G",
            value: `${data.defensiveReboundsPerGame?.blocksPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Steals / Game",
          value: `${data.stealsPerGame?.value}`,
          name: `${truncateName(data.stealsPerGame?.firstName)} ${data.stealsPerGame?.lastName}`,
          position: `${data.stealsPerGame?.position} ${data.stealsPerGame?.number}`,
          abbreviation: "STL/G",
          stat1: {
            label: "DR/G",
            value: `${data.stealsPerGame?.defensiveReboundsPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Blocks / Game",
          value: `${data.blocksPerGame?.value}`,
          name: `${truncateName(data.blocksPerGame?.firstName)} ${data.blocksPerGame?.lastName}`,
          position: `${data.blocksPerGame?.position} ${data.blocksPerGame?.number}`,
          abbreviation: "BLK/G",
          stat1: {
            label: "STL/G",
            value: `${data.blocksPerGame?.stealsPerGame}`,
          },
          sport: `${data.sport}`,
        },
      ];
    case "mlb":
      return [
        {
          label: "Batting Average",
          value: `${removeLeadingZeroDecimal(data.battingAverage.value as number)}`,
          name: `${truncateName(data.battingAverage.firstName)} ${data.battingAverage.lastName}`,
          position: `${data.battingAverage.position} ${data.battingAverage.number}`,
          abbreviation: "BA",
          stat1: {
            label: "HR",
            value: `${data.battingAverage.homeRuns}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Home Runs",
          value: `${data.homeRuns.value}`,
          name: `${truncateName(data.homeRuns.firstName)} ${data.homeRuns.lastName}`,
          position: `${data.homeRuns.position} ${data.homeRuns.number}`,
          abbreviation: "HR",
          stat1: {
            label: "RBI",
            value: `${data.homeRuns.runsBattedIn}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Runs Batted In",
          value: `${data.runsBattedIn.value}`,
          name: `${truncateName(data.runsBattedIn.firstName)} ${data.runsBattedIn.lastName}`,
          position: `${data.runsBattedIn.position} ${data.runsBattedIn.number}`,
          abbreviation: "RBI",
          stat1: {
            label: "BA",
            value: `${data.runsBattedIn.battingAverage}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Earned Run Average",
          value: `${data.earnedRunAverage?.value as number}`,
          name: `${truncateName(data.earnedRunAverage?.firstName)} ${data.earnedRunAverage?.lastName}`,
          position: `${data.earnedRunAverage?.position} ${data.earnedRunAverage?.number}`,
          abbreviation: "ERA",
          stat1: {
            label: "PSO",
            value: `${data.earnedRunAverage?.pitchingStrikeouts}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Pitching Strikeouts",
          value: `${data.pitchingStrikeouts?.value}`,
          name: `${truncateName(data.pitchingStrikeouts?.firstName)} ${data.pitchingStrikeouts?.lastName}`,
          position: `${data.pitchingStrikeouts?.position} ${data.pitchingStrikeouts?.number}`,
          abbreviation: "PSO",
          stat1: {
            label: "ERA",
            value: `${data.pitchingStrikeouts?.earnedRunAverage}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Saves",
          value: `${data.saves?.value}`,
          name: `${truncateName(data.saves?.firstName)} ${data.saves?.lastName}`,
          position: `${data.saves?.position} ${data.saves?.number}`,
          abbreviation: "SV",
          stat1: {
            label: "ERA",
            value: `${data.saves?.earnedRunAverage}`,
          },
          sport: `${data.sport}`,
        },
      ];
    case "nhl":
      return [
        {
          label: "Points",
          value: `${data.points.value}`,
          name: `${truncateName(data.points.firstName)} ${data.points.lastName}`,
          position: `${data.points.position} ${data.points.number}`,
          abbreviation: "PTS",
          stat1: {
            label: "G",
            value: `${data.points.goals}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Goals",
          value: `${data.goals.value}`,
          name: `${truncateName(data.goals.firstName)} ${data.goals.lastName}`,
          position: `${data.goals.position} ${data.goals.number}`,
          abbreviation: "G",
          stat1: {
            label: "PTS",
            value: `${data.goals.points}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Assists",
          value: `${data.assists.value}`,
          name: `${truncateName(data.assists.firstName)} ${data.assists.lastName}`,
          position: `${data.assists.position} ${data.assists.number}`,
          abbreviation: "A",
          stat1: {
            label: "PTS",
            value: `${data.assists.points}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Wins",
          value: `${data.wins?.value}`,
          name: `${truncateName(data.wins?.firstName)} ${data.wins?.lastName}`,
          position: `${data.wins?.position} ${data.wins?.number}`,
          abbreviation: "W",
          stat1: {
            label: "SV",
            value: `${data.wins?.saves}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Saves",
          value: `${data.saves?.value}`,
          name: `${truncateName(data.saves?.firstName)} ${data.saves?.lastName}`,
          position: `${data.saves?.position} ${data.saves?.number}`,
          abbreviation: "SV",
          stat1: {
            label: "GA/G",
            value: `${data.saves?.opponentGoalsPerGame}`,
          },
          sport: `${data.sport}`,
        },
        {
          label: "Goals Against / Game",
          value: `${data.opponentGoalsPerGame?.value}`,
          name: `${truncateName(data.opponentGoalsPerGame?.firstName)} ${data.opponentGoalsPerGame?.lastName}`,
          position: `${data.opponentGoalsPerGame?.position} ${data.opponentGoalsPerGame?.number}`,
          abbreviation: "GA/G",
          stat1: {
            label: "SV",
            value: `${data.opponentGoalsPerGame?.saves}`,
          },
          sport: `${data.sport}`,
        },
      ];
    default:
      return [];
  }
}
